// src/modules/app-views/account/pages/Init/modals/ChangePhoneModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useState } from 'react';
import { Alert, Typography } from 'antd';
import { MobileOutlined, NumberOutlined, WhatsAppOutlined } from '@ant-design/icons';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import appService from 'services/appService';
import authService from 'services/authService';
import translateX from 'utils/translateX';
import { getPhoneMeta } from 'utils/phone';

const { Text } = Typography;

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const VerifyCurrentPhoneStepContent = ({
  storedUser,
  currentPhone,
  currentPhoneCountryCode,
  codeSent,
  setCodeSent,
  isWhatsApp,
  setIsWhatsApp,
}) => {
  const [isSending, setIsSending] = useState(false);
  const phoneMeta = getPhoneMeta(currentPhone, currentPhoneCountryCode);

  // Send code automatically when step opens
  useEffect(() => {
    if (!codeSent && currentPhone && !isSending) {
      sendVerificationCode();
    }
  }, [codeSent, currentPhone]);

  const sendVerificationCode = async () => {
    if (isSending) return;

    setIsSending(true);

    try {
      const result = await authService.sendSmsRegistrationCode({
        name: storedUser?.name,
        mobile: currentPhone,
        mobileCountryCode: currentPhoneCountryCode,
      });

      if (result?.success) {
        const channel = result?.channel || 'sms';
        setIsWhatsApp(channel === 'whatsapp');
        setCodeSent(true);

        appService.notification({
          key: 'phoneCodeSentKey',
          flag: 'info',
          title: phoneMeta?.national || currentPhone,
          description: channel === 'whatsapp'
            ? 'verification_code_sent_whatsapp'
            : 'verification_code_sent_sms',
          duration: 0,
          icon: channel === 'whatsapp'
            ? <WhatsAppOutlined style={{ color: '#25D366' }} />
            : <MobileOutlined />,
        });
      } else {
        await appService.message({ flag: 'e', text: 'failed_to_send_verification_code' });
      }
    } catch (err) {
      console.error('[ChangePhoneModal] Error sending code:', err);
      await appService.message({ flag: 'e', text: 'failed_to_send_verification_code' });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <Alert
        type="info"
        showIcon
        icon={isWhatsApp ? <WhatsAppOutlined style={{ color: '#25D366' }} /> : <MobileOutlined />}
        title={translateX('verify_current_phone')}
        description={translateX('verify_current_phone_description', {
          '%PHONE%': phoneMeta?.national || currentPhone,
        })}
        style={{ marginBottom: 16 }}
      />

      <SimpleInput
        name="currentPhoneCode"
        label="verification_code"
        minLength={4}
        maxLength={4}
        required
        prefix={<NumberOutlined className="text-primary" />}
        placeholder="0000"
      />

      {codeSent && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {translateX('didnt_receive_code')}{' '}
          <a onClick={sendVerificationCode} style={{ cursor: isSending ? 'wait' : 'pointer' }}>
            {translateX('resend_code')}
          </a>
        </Text>
      )}
    </>
  );
};

// *******************************************************************************
// HELPERS
// *******************************************************************************
const validateCurrentPhoneCode = async ({
  form,
  setLoading,
  goFocus,
  currentPhone,
  currentPhoneCountryCode,
}) => {
  const code = form?.getFieldValue('currentPhoneCode');

  if (!code || code.trim() === '') {
    await appService.message({ flag: 'w', text: 'verification_code_required' });
    goFocus?.('currentPhoneCode', true);
    return false;
  }

  try {
    setLoading(true);

    const success = await authService.validateSmsCode({
      mobile: currentPhone,
      mobileCountryCode: currentPhoneCountryCode,
      mobileCode: code,
    });

    if (success) {
      appService.notificationDestroy('phoneCodeSentKey');
      return true;
    } else {
      await appService.message({ flag: 'w', text: 'invalid_verification_code' });
      goFocus?.('currentPhoneCode', true);
      return false;
    }
  } catch (err) {
    console.error('[ChangePhoneModal] Error validating code:', err);
    await appService.message({ flag: 'e', text: 'verification_failed' });
    goFocus?.('currentPhoneCode', true);
    return false;
  } finally {
    setLoading(false);
  }
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const verifyCurrentPhoneStep = ({
  storedUser,
  currentPhone,
  currentPhoneCountryCode,
}) => {
  // Local state for the step (will be managed via closure)
  let codeSent = false;
  let isWhatsApp = false;

  return {
    stepTitle: 'current_phone',
    stepTooltip: 'verify_current_phone',
    content: ({ form, goFocus }) => {
      const [localCodeSent, setLocalCodeSent] = useState(codeSent);
      const [localIsWhatsApp, setLocalIsWhatsApp] = useState(isWhatsApp);

      // Sync with closure
      useEffect(() => {
        codeSent = localCodeSent;
        isWhatsApp = localIsWhatsApp;
      }, [localCodeSent, localIsWhatsApp]);

      return (
        <VerifyCurrentPhoneStepContent
          storedUser={storedUser}
          currentPhone={currentPhone}
          currentPhoneCountryCode={currentPhoneCountryCode}
          codeSent={localCodeSent}
          setCodeSent={setLocalCodeSent}
          isWhatsApp={localIsWhatsApp}
          setIsWhatsApp={setLocalIsWhatsApp}
        />
      );
    },
    validationFields: ['currentPhoneCode'],
    onNext: async ({ form, setLoading, goFocus }) =>
      validateCurrentPhoneCode({
        form,
        setLoading,
        goFocus,
        currentPhone,
        currentPhoneCountryCode,
      }),
    onEnter: ({ goFocus }) => {
      setTimeout(() => goFocus?.('currentPhoneCode'), 100);
    },
  };
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default verifyCurrentPhoneStep;
